import React, { useMemo, useState } from "react";
import { makeAPI } from "../../services/api";
import { getViteImage } from "../../utils/utils";

const INITIAL_HANDLES = {
    chess: "",
    clash_royal: "",
};

const HELP_CONTENT = {
    chess: {
        title: "Where to find Chess.com ID",
        steps: [
            "Open Chess.com app or website and login.",
            "Go to your profile page.",
            "Your username shown in profile is your Chess ID.",
        ],
        img_urls:["chess1"]
    },
    clash_royal: {
        title: "Where to find Clash Royale Tag",
        steps: [
            "Open Clash Royale.",
            "Tap your profile icon.",
            "Copy your player tag (starts with #).",
        ],
        img_urls:["cor1"]
    },
};

function normalizeHandles(data) {
    return {
        chess: (data?.chess || "").trim(),
        clash_royal: (data?.clash_royal || "").trim(),
    };
}

export default function GamerHandleChange() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const [formData, setFormData] = useState(normalizeHandles(user?.gamer_handle || INITIAL_HANDLES));
    const [initialData, setInitialData] = useState(normalizeHandles(user?.gamer_handle || INITIAL_HANDLES));
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [selectedHelpField, setSelectedHelpField] = useState("chess");

    function validateField(field, value) {
        const trimmed = (value || "").trim();

        if (!trimmed) return "";

        if (field === "clash_royal") {
            if (!trimmed.startsWith("#")) return "Clash Royale ID must start with #.";
            if (!/^#[A-Za-z0-9]+$/.test(trimmed)) return "Only letters/numbers allowed after #.";
        }

        return "";
    }

    function validateForm(nextForm = formData) {
        const nextErrors = {};
        Object.keys(nextForm).forEach((key) => {
            const msg = validateField(key, nextForm[key]);
            if (msg) nextErrors[key] = msg;
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }

    function handleFieldChange(field, value) {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            const message = validateField(field, value);
            setErrors((prevErrors) => ({ ...prevErrors, [field]: message || undefined }));
            return next;
        });
    }

    const hasFormChanges = useMemo(
        () => JSON.stringify(normalizeHandles(formData)) !== JSON.stringify(normalizeHandles(initialData)),
        [formData, initialData]
    );

    function openHelpModal(field) {
        setSelectedHelpField(field);
        if (window.UIkit?.modal) {
            window.UIkit.modal("#modal-gamer-help", { container: false }).show();
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setApiError("");
        setApiSuccess("");

        const cleaned = normalizeHandles(formData);
        if (!validateForm(cleaned)) return;
        if (!hasFormChanges) {
            setApiError("No changes detected.");
            return;
        }

        try {
            setIsSubmitting(true);
            const userId = user?.user_id;
            const payload = {
                user_id: userId,
                inputobj: {
                    chess: cleaned.chess.toLowerCase(),
                    clash_royal: cleaned.clash_royal.toUpperCase(),
                },
            };

            const response = await makeAPI("/api/gamerhandle_change", "post", false, payload);
            if (response?.status === "S") {
                const nextInitial = normalizeHandles(payload.inputobj);
                setInitialData(nextInitial);
                setFormData(nextInitial);

                const updatedUser = { ...user, gamer_handle: { ...(user?.gamer_handle || {}), ...nextInitial } };
                localStorage.setItem("user", JSON.stringify(updatedUser));

                setApiSuccess("Gamer handles updated successfully.");
            } else {
                setApiError(response?.message || "Unable to update gamer handles.");
            }
        } catch (error) {
            setApiError("Something went wrong while saving gamer handles.");
        } finally {
            setIsSubmitting(false);
        }
    }

    const helpData = HELP_CONTENT[selectedHelpField] || HELP_CONTENT.chess;

    return (
        <>
            <style>{`
                .gamer-handle-wrap .form-login { margin: 0 auto; max-width: 780px; width: 100%; }
                .gamer-handle-wrap .form-login__box { width: 100%; }
                body.dark-theme .gamer-handle-wrap .uk-input {
                    color: #e4e4e4;
                    background: #000;
                    border: 1px solid #3d3d3d;
                }
                body.dark-theme .gamer-handle-wrap .uk-input::placeholder { color: #9ea0a6; }
                .gamer-handle-wrap .uk-alert-danger {
                    background: #000;
                    padding: 8px;
                    color: #ff4a4a;
                    border-radius: 12px;
                    text-align: center;
                }
                .gamer-handle-wrap .uk-alert-success {
                    background: #000;
                    padding: 8px;
                    color: #43d17b;
                    border-radius: 12px;
                    text-align: center;
                }
                .gamer-help-images {
                    display: grid;
                    gap: 10px;
                    margin-top: 12px;
                }
                .gamer-help-images img {
                    width: 100%;
                    border-radius: 8px;
                    border: 1px solid #2d3034;
                }
            `}</style>

            <div className="gamer-handle-wrap uk-padding-small uk-padding-medium@m">
                <div className="uk-flex uk-flex-center">
                    <div className="form-login p-0">
                        <div className="form-login__box">
                            <h5 className="text-light mb-3 uk-nav-header">Gamer Handles</h5>

                            {apiError ? <div className="uk-alert-danger uk-margin-small">{apiError}</div> : null}
                            {apiSuccess ? <div className="uk-alert-success uk-margin-small">{apiSuccess}</div> : null}

                            <form onSubmit={handleSubmit}>
                                <div className="uk-margin">
                                    <label className="form-label text-light">Chess.com</label>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className={`uk-input pe-5 ${errors.chess ? "is-invalid" : ""}`}
                                            placeholder="Enter your Chess.com ID"
                                            value={formData.chess}
                                            onChange={(e) => handleFieldChange("chess", e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="position-absolute bg-transparent border-0 p-0"
                                            style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
                                            onClick={() => openHelpModal("chess")}
                                        >
                                            <i className="fa fa-info-circle" style={{ fontSize: "18px", color: "#fff" }}></i>
                                        </button>
                                    </div>
                                    {errors.chess ? <div className="invalid-feedback d-block">{errors.chess}</div> : null}
                                </div>

                                <div className="uk-margin">
                                    <label className="form-label text-light">Clash Royale</label>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className={`uk-input pe-5 ${errors.clash_royal ? "is-invalid" : ""}`}
                                            placeholder="Enter your Clash Royale tag (#...)"
                                            value={formData.clash_royal}
                                            onChange={(e) => handleFieldChange("clash_royal", e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="position-absolute bg-transparent border-0 p-0"
                                            style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
                                            onClick={() => openHelpModal("clash_royal")}
                                        >
                                            <i className="fa fa-info-circle" style={{ fontSize: "18px", color: "#fff" }}></i>
                                        </button>
                                    </div>
                                    {errors.clash_royal ? <div className="invalid-feedback d-block">{errors.clash_royal}</div> : null}
                                </div>

                                <div className="uk-margin">
                                    <button
                                        type="submit"
                                        className="uk-button uk-width-1-1 text-white"
                                        style={{ backgroundColor: hasFormChanges ? "#ff820e" : "#f5f3f309",cursor:hasFormChanges ? "pointer" :"not-allowed" }}
                                        disabled={isSubmitting || !hasFormChanges}
                                    >
                                        {isSubmitting ? "Saving..." : "Save Gamer Handles"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <div className="uk-flex-top" id="modal-gamer-help" data-uk-modal="container: false">
                <div className="uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
                    <button className="uk-modal-close-default text-white" type="button" data-uk-close style={{border:" 1px solid #ff810e"}}></button>
                    <div className="form-login" style={{ margin: 0, maxWidth: "100%" }}>
                        <div className="form-login__box">
                            <h4 className="uk-margin-small-bottom">{helpData.title}</h4>
                            <ul className="uk-list uk-list-disc uk-margin-remove-top">
                                {helpData.steps.map((step) => (
                                    <li key={step}>{step}</li>
                                ))}
                            </ul>
                            {helpData.img_urls?.length ? (
                                <div className="gamer-help-images">
                                    {helpData.img_urls.map((imgUrl) => (
                                        <img key={imgUrl} src={getViteImage(`reference/${imgUrl}.avif`)} alt={`${helpData.title} reference`} />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
